import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Send, Download, ListPlus, ChevronLeft, ChevronRight, Search, Plus, Trash2, Mail, Loader2, RefreshCw, QrCode, UploadCloud, FileDown, AlertCircle, CheckCircle2 } from "lucide-react";

import { api, API_BASE, apiErrorMessage, downloadAuthenticatedFile } from "@/lib/api";
import { canWrite } from "@/lib/auth";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const QUEUE_LABELS = { PRIORITY: "Priority", NORMAL: "Normal", LATE: "Late" };
const PAGE_SIZE = 25;
const BULK_SELECT_PAGE_SIZE = 25;
const BULK_EMAIL_CHUNK_SIZE = 5;
const QR_EMAIL_TIMEOUT_MS = 180000;
const QR_EMAIL_STATUS = {
  sent: { label: "Sent", className: "bg-emerald-600" },
  failed: { label: "Failed", className: "bg-red-600" },
  skipped: { label: "Skipped", className: "bg-amber-600" },
};

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function Registrations() {
  const readOnly = !canWrite();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [addQueueType, setAddQueueType] = useState("NORMAL");
  const [guestForm, setGuestForm] = useState(null);
  const [savingGuests, setSavingGuests] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkIncludeSent, setBulkIncludeSent] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkBatches, setBulkBatches] = useState([]);
  const [bulkBatchDetail, setBulkBatchDetail] = useState(null);
  const [bulkLoadingBatches, setBulkLoadingBatches] = useState(false);
  const [bulkTab, setBulkTab] = useState("all");
  const [bulkSelectRows, setBulkSelectRows] = useState([]);
  const [bulkSelectTotal, setBulkSelectTotal] = useState(0);
  const [bulkSelectPage, setBulkSelectPage] = useState(1);
  const [bulkSelectQ, setBulkSelectQ] = useState("");
  const [bulkSelectDebouncedQ, setBulkSelectDebouncedQ] = useState("");
  const [bulkSelectLoading, setBulkSelectLoading] = useState(false);
  const [bulkSelectedIds, setBulkSelectedIds] = useState(() => new Set());
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [guestCountFile, setGuestCountFile] = useState(null);
  const [guestCountReport, setGuestCountReport] = useState(null);
  const [guestCountBusy, setGuestCountBusy] = useState(false);
  const [guestCountCommitted, setGuestCountCommitted] = useState(false);
  const [qrPreview, setQrPreview] = useState(null);
  const [qrPreviewLoading, setQrPreviewLoading] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => { setDebouncedQ(q); setPage(1); }, 350);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    const id = setTimeout(() => { setBulkSelectDebouncedQ(bulkSelectQ); setBulkSelectPage(1); }, 350);
    return () => clearTimeout(id);
  }, [bulkSelectQ]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/registrations", { params: { q: debouncedQ, page, page_size: PAGE_SIZE } });
      setRows(r.data.items);
      setTotal(r.data.total);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [debouncedQ, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const viewDetails = async (r) => {
    try {
      const res = await api.get(`/registrations/${r.id}`);
      setDetail(res.data);
      setGuestForm(res.data.guests && res.data.guests.length > 0
        ? res.data.guests.map((g) => ({ name: g.name || "", relation: g.relation || "", phone: g.phone || "" }))
        : []);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const addGuestRow = () => setGuestForm((g) => [...(g || []), { name: "", relation: "", phone: "" }]);
  const removeGuestRow = (idx) => setGuestForm((g) => g.filter((_, i) => i !== idx));
  const updateGuestRow = (idx, field, value) =>
    setGuestForm((g) => g.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));

  const saveGuests = async () => {
    if (!detail || !guestForm) return;
    setSavingGuests(true);
    try {
      const r = await api.patch(`/registrations/${detail.id}`, { guests: guestForm });
      toast.success("Guest details saved");
      setDetail({ ...detail, guests: guestForm, guest_count: guestForm.length });
      load();
      return r;
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSavingGuests(false);
    }
  };

  const saveGuestsAndSendQR = async () => {
    await saveGuests();
    if (detail) await sendQR(detail);
  };

  const sendQR = async (reg) => {
    try {
      const r = await api.post(`/registrations/${reg.id}/send-qr`, null, { timeout: QR_EMAIL_TIMEOUT_MS });
      const email = r.data.email;
      if (email?.sent) {
        toast.success(`QR emailed to ${email.to}`);
      } else if (email?.detail) {
        toast.warning(`QR generated — email not sent: ${email.detail}`);
      } else {
        toast.success(`QR generated (${r.data.qrs.length})`);
      }
      if (detail && detail.id === reg.id) setDetail({ ...detail, qrs: r.data.qrs });
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const viewQR = async (reg) => {
    setQrPreview({
      registration_id: reg.id,
      htno: reg.htno,
      student_name: reg.student_name,
      qrs: [],
    });
    setQrPreviewLoading(true);
    try {
      const r = await api.get(`/registrations/${reg.id}/qrs`);
      setQrPreview(r.data);
      if (detail && detail.id === reg.id) setDetail({ ...detail, qrs: r.data.qrs });
    } catch (e) {
      setQrPreview(null);
      toast.error(apiErrorMessage(e));
    } finally {
      setQrPreviewLoading(false);
    }
  };

  const togglePriority = async (r, v) => {
    try {
      await api.patch(`/registrations/${r.id}`, { is_priority: v });
      toast.success(v ? "Marked as priority" : "Priority removed");
      load();
      if (detail && detail.id === r.id) setDetail({ ...detail, is_priority: v });
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const toggleExclude = async (r, excluded) => {
    try {
      await api.patch(`/registrations/${r.id}`, { include_in_sequence: !excluded });
      toast.success(excluded ? "Excluded from sequence" : "Included in sequence");
      load();
      if (detail && detail.id === r.id) setDetail({ ...detail, include_in_sequence: !excluded });
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const addToSequence = async (r) => {
    try {
      await api.post(`/registrations/${r.id}/add-to-sequence`, { queue_type: addQueueType });
      toast.success(`Added to ${QUEUE_LABELS[addQueueType]} queue`);
      load();
      if (detail && detail.id === r.id) viewDetails(r);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const exportRegistrations = () => {
    const params = new URLSearchParams();
    if (debouncedQ) params.set("q", debouncedQ);
    window.open(`${API_BASE}/registrations/export?${params.toString()}`, "_blank");
  };

  const loadBulkBatches = async () => {
    setBulkLoadingBatches(true);
    try {
      const r = await api.get("/registrations/qr-email/batches", { params: { limit: 15 } });
      setBulkBatches(r.data.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBulkLoadingBatches(false);
    }
  };

  const openBulkDialog = () => {
    setBulkOpen(true);
    setBulkResult(null);
    setBulkBatchDetail(null);
    setBulkTab("upload");
    setBulkSelectQ("");
    setBulkSelectDebouncedQ("");
    setBulkSelectPage(1);
    setBulkSelectedIds(new Set());
    setGuestCountFile(null);
    setGuestCountReport(null);
    setGuestCountCommitted(false);
    loadBulkBatches();
  };

  const loadBulkSelectRows = async () => {
    setBulkSelectLoading(true);
    try {
      const r = await api.get("/registrations", {
        params: {
          q: bulkSelectDebouncedQ,
          page: bulkSelectPage,
          page_size: BULK_SELECT_PAGE_SIZE,
          attending_only: true,
        },
      });
      setBulkSelectRows(r.data.items || []);
      setBulkSelectTotal(r.data.total || 0);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBulkSelectLoading(false);
    }
  };

  useEffect(() => {
    if (!bulkOpen || bulkTab !== "select") return;
    loadBulkSelectRows();
  }, [bulkOpen, bulkTab, bulkSelectDebouncedQ, bulkSelectPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleBulkSelect = (regId, checked) => {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(regId);
      else next.delete(regId);
      return next;
    });
  };

  const toggleBulkSelectPage = (checked) => {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      bulkSelectRows.forEach((r) => {
        if (checked) next.add(r.id);
        else next.delete(r.id);
      });
      return next;
    });
  };

  const bulkSelectPageAllSelected = bulkSelectRows.length > 0
    && bulkSelectRows.every((r) => bulkSelectedIds.has(r.id));
  const bulkSelectPages = Math.max(1, Math.ceil(bulkSelectTotal / BULK_SELECT_PAGE_SIZE));

  const loadBatchDetail = async (batchId) => {
    try {
      const r = await api.get(`/registrations/qr-email/batches/${batchId}`);
      setBulkBatchDetail(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const collectBulkTargetIds = async () => {
    const ids = [];
    let pageNum = 1;
    const pageSize = 100;
    while (true) {
      const r = await api.get("/registrations", {
        params: { page: pageNum, page_size: pageSize, attending_only: true },
      });
      const items = r.data.items || [];
      for (const row of items) {
        if (bulkIncludeSent || row.qr_email_status !== "sent") {
          ids.push(row.id);
        }
      }
      const totalCount = r.data.total || 0;
      if (pageNum * pageSize >= totalCount || items.length === 0) break;
      pageNum += 1;
    }
    return ids;
  };

  const collectResendFailedIds = async (batchId) => {
    const r = await api.get(`/registrations/qr-email/batches/${batchId}`);
    return (r.data.items || [])
      .filter((item) => item.status === "failed")
      .map((item) => item.registration_id);
  };

  const runBulkSendChunks = async (registrationIds, { includeAlreadySent = false } = {}) => {
    const chunks = [];
    for (let i = 0; i < registrationIds.length; i += BULK_EMAIL_CHUNK_SIZE) {
      chunks.push(registrationIds.slice(i, i + BULK_EMAIL_CHUNK_SIZE));
    }
    if (chunks.length === 0) {
      toast.error("No students to email.");
      return;
    }

    let batchId = null;
    const aggregated = {
      batch_id: null,
      status: "running",
      total: registrationIds.length,
      sent: 0,
      failed: 0,
      skipped: 0,
      items: [],
    };

    for (let i = 0; i < chunks.length; i += 1) {
      const chunk = chunks[i];
      const isLast = i === chunks.length - 1;
      const r = await api.post(
        "/registrations/qr-email/bulk",
        {
          registration_ids: chunk,
          include_already_sent: includeAlreadySent,
          batch_id: batchId || undefined,
          expected_total: batchId ? undefined : registrationIds.length,
          finalize_batch: isLast,
        },
        { timeout: QR_EMAIL_TIMEOUT_MS },
      );
      batchId = r.data.batch_id;
      aggregated.batch_id = batchId;
      aggregated.sent = r.data.sent ?? aggregated.sent;
      aggregated.failed = r.data.failed ?? aggregated.failed;
      aggregated.skipped = r.data.skipped ?? aggregated.skipped;
      aggregated.items = aggregated.items.concat(r.data.items || []);
      aggregated.status = r.data.status || (isLast ? "completed" : "running");
      setBulkResult({ ...aggregated });
    }

    return aggregated;
  };

  const runBulkGenerate = async ({ registrationIds } = {}) => {
    if (registrationIds && registrationIds.length === 0) {
      toast.error("Select at least one student to generate QRs.");
      return;
    }
    setBulkGenerating(true);
    try {
      const payload = registrationIds?.length ? { registration_ids: registrationIds } : {};
      const r = await api.post("/registrations/qr/generate/bulk", payload);
      toast.success(
        `Created ${r.data.qr_count} new QR pass(es) for ${r.data.generated} student(s)`
        + (r.data.unchanged ? ` — ${r.data.unchanged} already had all QRs` : "")
        + ". Visible on /pass without email.",
      );
      if (r.data.failed) toast.warning(`${r.data.failed} student(s) failed`);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBulkGenerating(false);
    }
  };

  const validateGuestCountUpload = async () => {
    if (!guestCountFile) {
      toast.error("Choose an Excel file first");
      return;
    }
    setGuestCountBusy(true);
    setGuestCountReport(null);
    setGuestCountCommitted(false);
    try {
      const fd = new FormData();
      fd.append("file", guestCountFile);
      const r = await api.post("/registrations/guest-count/validate", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setGuestCountReport(r.data);
      toast.success(
        `Validated: ${r.data.valid_count} to update, ${r.data.unchanged_count} unchanged, ${r.data.invalid_count} invalid`,
      );
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setGuestCountBusy(false);
    }
  };

  const commitGuestCountUpload = async () => {
    if (!guestCountReport?.valid_count) return;
    setGuestCountBusy(true);
    try {
      const rows = guestCountReport.valid_rows.map((row) => ({
        registration_id: row.registration_id,
        htno: row.htno,
        new_guest_count: row.new_guest_count,
        current_guest_count: row.current_guest_count,
      }));
      const r = await api.post("/registrations/guest-count/commit", { rows });
      toast.success(
        `Added ${r.data.guests_added} guest slot(s) and created ${r.data.qr_count} new QR pass(es) for ${r.data.updated} student(s)`,
      );
      if (r.data.failed) toast.warning(`${r.data.failed} row(s) failed`);
      setGuestCountCommitted(true);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setGuestCountBusy(false);
    }
  };

  const downloadGuestCountTemplate = async () => {
    try {
      await downloadAuthenticatedFile(
        "/registrations/guest-count/template",
        "guest_count_upload_template.xlsx",
      );
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const runBulkSend = async ({ resendFailedBatchId, registrationIds } = {}) => {
    if (registrationIds && registrationIds.length === 0) {
      toast.error("Select at least one student to send QR emails.");
      return;
    }
    setBulkRunning(true);
    setBulkResult(null);
    try {
      let targetIds = registrationIds;
      if (resendFailedBatchId) {
        targetIds = await collectResendFailedIds(resendFailedBatchId);
      } else if (!targetIds?.length) {
        targetIds = await collectBulkTargetIds();
      }
      if (!targetIds?.length) {
        toast.error("No students to email.");
        return;
      }

      const result = await runBulkSendChunks(targetIds, {
        includeAlreadySent: Boolean(resendFailedBatchId || bulkIncludeSent),
      });
      setBulkBatchDetail(null);
      await loadBulkBatches();
      load();
      toast.success(`Bulk send complete — ${result.sent} sent, ${result.failed} failed, ${result.skipped} skipped`);
    } catch (e) {
      const msg = apiErrorMessage(e);
      toast.error(msg.includes("timeout") ? `${msg} — try again; partial sends are logged in batch history.` : msg);
      await loadBulkBatches();
    } finally {
      setBulkRunning(false);
    }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div data-testid="registrations-page">
      <PageHeader
        title="Registrations"
        subtitle="Full list of registered students for the active convocation."
        actions={
          <div className="flex items-center gap-2">
            {!readOnly && (
              <Button variant="outline" data-testid="bulk-send-qr-btn" onClick={openBulkDialog}>
                <Mail size={14} className="mr-1" /> Bulk Send QR
              </Button>
            )}
            <Button variant="outline" data-testid="export-registrations-btn" onClick={exportRegistrations}>
              <Download size={14} className="mr-1" /> Export
            </Button>
          </div>
        }
      />
      <div className="bg-white border border-border rounded-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border gap-3">
          <div className="relative w-full md:w-96">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="registrations-search"
              className="pl-8" placeholder="Search by roll no. or name…" value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <p className="text-sm text-slate-500 shrink-0">
            {total} record{total === 1 ? "" : "s"} · Page {page} / {pages}
          </p>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Roll</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-center">QR Email</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Program</TableHead>
                <TableHead className="text-center">Attending</TableHead>
                <TableHead className="text-center">Guests</TableHead>
                <TableHead className="text-center">Student CI</TableHead>
                <TableHead className="text-center">Guest CI</TableHead>
                <TableHead className="text-center">Priority</TableHead>
                <TableHead className="text-center">Excluded</TableHead>
                <TableHead>Queue</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={14} className="text-center py-10 text-slate-500">Loading…</TableCell></TableRow>
              )}
              {!loading && rows.map((r) => (
                <TableRow key={r.id} data-testid={`reg-row-${r.htno}`}>
                  <TableCell className="font-medium">{r.htno}</TableCell>
                  <TableCell>{r.student_name}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{r.mu_email || r.email || "—"}</TableCell>
                  <TableCell className="text-center">
                    {r.qr_email_status ? (
                      <Badge className={QR_EMAIL_STATUS[r.qr_email_status]?.className || "bg-slate-500"}>
                        {QR_EMAIL_STATUS[r.qr_email_status]?.label || r.qr_email_status}
                      </Badge>
                    ) : (
                      <span className="text-slate-400 text-xs">Not sent</span>
                    )}
                  </TableCell>
                  <TableCell>{r.entity}</TableCell>
                  <TableCell>{r.program}</TableCell>
                  <TableCell className="text-center">
                    {r.attending ? <Badge className="bg-emerald-600">Yes</Badge> : <Badge variant="secondary">No</Badge>}
                  </TableCell>
                  <TableCell className="text-center">{r.guest_count}</TableCell>
                  <TableCell className="text-center">
                    {r.student_checked_in ? <Badge className="bg-emerald-600">Yes</Badge> : <Badge variant="secondary">No</Badge>}
                  </TableCell>
                  <TableCell className="text-center">{r.guest_checked_in_count}</TableCell>
                  <TableCell className="text-center">
                    {readOnly ? (
                      r.is_priority ? <Badge className="bg-amber-600">Yes</Badge> : <Badge variant="secondary">No</Badge>
                    ) : (
                      <Switch
                        data-testid={`priority-toggle-${r.htno}`}
                        checked={!!r.is_priority}
                        onCheckedChange={(v) => togglePriority(r, v)}
                      />
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {readOnly ? (
                      !r.include_in_sequence ? <Badge variant="secondary">Yes</Badge> : <Badge className="bg-emerald-600">No</Badge>
                    ) : (
                      <Switch
                        data-testid={`exclude-toggle-${r.htno}`}
                        checked={!r.include_in_sequence}
                        onCheckedChange={(v) => toggleExclude(r, v)}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {r.sequence_status ? (
                      <Badge variant="outline">{QUEUE_LABELS[r.sequence_queue_type]} · {r.sequence_status}</Badge>
                    ) : (
                      <span className="text-slate-400 text-xs">Not queued</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" data-testid={`view-btn-${r.htno}`} onClick={() => viewDetails(r)}>
                      <Eye size={14} className="mr-1" /> View
                    </Button>
                    <Button
                      variant="outline" size="sm" data-testid={`reg-view-qr-${r.htno}`} className="ml-2"
                      onClick={() => viewQR(r)}
                    >
                      <QrCode size={14} className="mr-1" /> View QR
                    </Button>
                    {!readOnly && (
                      <Button
                        size="sm" data-testid={`reg-send-qr-${r.htno}`} className="ml-2 bg-primary hover:bg-primary/90"
                        onClick={() => sendQR(r)}
                      >
                        <Send size={14} className="mr-1" /> Send QR
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={14} className="text-center py-10 text-slate-500">No records</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between px-6 py-3 border-t border-border">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="registrations-prev">
            <ChevronLeft size={14} /> Prev
          </Button>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="registrations-next">
            Next <ChevronRight size={14} />
          </Button>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setGuestForm(null); } }}>
        <DialogContent className="max-w-3xl bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {detail?.student_name} <span className="text-slate-400 text-base ml-2">{detail?.htno}</span>
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div><p className="kicker">Email</p><p>{detail.email || "—"}</p></div>
                <div><p className="kicker">Entity</p><p>{detail.entity || "—"}</p></div>
                <div><p className="kicker">Program</p><p>{detail.program || "—"}</p></div>
                <div><p className="kicker">Specialization</p><p>{detail.specialization || "—"}</p></div>
                <div><p className="kicker">Gender</p><p>{detail.gender || "—"}</p></div>
                <div><p className="kicker">School (as submitted)</p><p>{detail.school || "—"}</p></div>
                <div><p className="kicker">Attending</p><p>{detail.attending ? "Yes" : "No"}</p></div>
                <div><p className="kicker">Mobile 1</p><p>{detail.mobile_1 || "—"}</p></div>
                <div><p className="kicker">Mobile 2</p><p>{detail.mobile_2 || "—"}</p></div>
                <div><p className="kicker">Residential Address</p><p>{detail.residential_address || "—"}</p></div>
                <div><p className="kicker">Hostel Required</p><p>{detail.hostel_required ? "Yes" : "No"}</p></div>
                <div><p className="kicker">Arrival</p><p>{detail.arrival_datetime || "—"}</p></div>
                <div><p className="kicker">Departure</p><p>{detail.departure_datetime || "—"}</p></div>
                <div><p className="kicker">Farewell Dinner</p><p>{detail.farewell_dinner ? "Yes" : "No"}</p></div>
                <div><p className="kicker">Farewell Transport</p><p>{detail.transport_required ? "Yes" : "No"}</p></div>
                <div><p className="kicker">Guests</p><p>{detail.guest_count}</p></div>
              </div>

              {guestForm && (
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="kicker">Guest Details · {guestForm.length}</p>
                    {!readOnly && (
                      <Button
                        size="sm" variant="outline" data-testid="add-guest-btn"
                        onClick={addGuestRow}
                      >
                        <Plus size={14} className="mr-1" /> Add Guest
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    {guestForm.map((guest, idx) => (
                      <div key={idx} className="border rounded-md p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="font-medium">Guest {idx + 1}</p>
                          {!readOnly && (
                            <Button
                              size="icon" variant="ghost" className="h-6 w-6"
                              data-testid={`remove-guest-${idx}`}
                              onClick={() => removeGuestRow(idx)}
                            >
                              <Trash2 size={14} className="text-slate-400" />
                            </Button>
                          )}
                        </div>
                        {readOnly ? (
                          <>
                            <p><span className="text-slate-500">Name:</span> {guest.name || "—"}</p>
                            <p><span className="text-slate-500">Relation:</span> {guest.relation || "—"}</p>
                            <p><span className="text-slate-500">Phone:</span> {guest.phone || "—"}</p>
                          </>
                        ) : (
                          <>
                            <Input
                              data-testid={`guest-name-input-${idx}`}
                              placeholder="Name" value={guest.name}
                              onChange={(e) => updateGuestRow(idx, "name", e.target.value)}
                            />
                            <Input
                              data-testid={`guest-relation-input-${idx}`}
                              placeholder="Relation" value={guest.relation}
                              onChange={(e) => updateGuestRow(idx, "relation", e.target.value)}
                            />
                            <Input
                              data-testid={`guest-phone-input-${idx}`}
                              placeholder="Phone" value={guest.phone}
                              onChange={(e) => updateGuestRow(idx, "phone", e.target.value)}
                            />
                          </>
                        )}
                      </div>
                    ))}
                    {guestForm.length === 0 && (
                      <p className="text-slate-400 text-sm">No guests added yet.</p>
                    )}
                  </div>
                  {!readOnly && (
                    <div className="flex justify-end gap-2 mt-3">
                      <Button
                        size="sm" variant="outline" data-testid="save-guests-btn"
                        disabled={savingGuests} onClick={saveGuests}
                      >
                        Save Guest Details
                      </Button>
                      <Button
                        size="sm" data-testid="save-guests-send-qr-btn" className="bg-primary hover:bg-primary/90"
                        disabled={savingGuests} onClick={saveGuestsAndSendQR}
                      >
                        <Send size={14} className="mr-1" /> Save & Send QR
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center gap-8 border-t pt-4">
                <div className="flex items-center gap-2">
                  {readOnly ? (
                    <>
                      <span className="text-sm">Priority / Gold Medalist:</span>
                      <Badge variant={detail.is_priority ? "default" : "secondary"}>{detail.is_priority ? "Yes" : "No"}</Badge>
                    </>
                  ) : (
                    <>
                      <Switch checked={!!detail.is_priority} onCheckedChange={(v) => togglePriority(detail, v)} />
                      <span className="text-sm">Priority / Gold Medalist</span>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {readOnly ? (
                    <>
                      <span className="text-sm">Excluded from sequence:</span>
                      <Badge variant={!detail.include_in_sequence ? "default" : "secondary"}>{!detail.include_in_sequence ? "Yes" : "No"}</Badge>
                    </>
                  ) : (
                    <>
                      <Switch checked={!detail.include_in_sequence} onCheckedChange={(v) => toggleExclude(detail, v)} />
                      <span className="text-sm">Excluded from sequence</span>
                    </>
                  )}
                </div>
              </div>

              <div className="border-t pt-4">
                <p className="kicker mb-2">Stage Sequence</p>
                {detail.sequence_status ? (
                  <Badge variant="outline">{QUEUE_LABELS[detail.sequence_queue_type]} · {detail.sequence_status}</Badge>
                ) : readOnly ? (
                  <span className="text-slate-400 text-sm">Not queued</span>
                ) : (
                  <div className="flex items-center gap-2">
                    <Select value={addQueueType} onValueChange={setAddQueueType}>
                      <SelectTrigger className="w-40" data-testid="add-sequence-queue-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PRIORITY">Priority</SelectItem>
                        <SelectItem value="NORMAL">Normal</SelectItem>
                        <SelectItem value="LATE">Late</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm" variant="outline" data-testid="add-to-sequence-btn"
                      onClick={() => addToSequence(detail)}
                    >
                      <ListPlus size={14} className="mr-1" /> Add to Sequence
                    </Button>
                  </div>
                )}
              </div>

              {detail.qrs && detail.qrs.length > 0 && (
                <div className="border-t pt-4">
                  <p className="kicker mb-2">QR Codes</p>
                  <div className="grid grid-cols-3 gap-4">
                    {detail.qrs.map((q) => {
                      const label = q.qr_type === "GUEST" ? `Guest ${q.guest_index}` : "Student";
                      return (
                        <div key={q.id} className="text-center border rounded-md p-3">
                          <p className="text-xs font-medium mb-2">{label}</p>
                          {q.image && <img src={q.image} alt={label} className="mx-auto w-full max-w-[160px]" />}
                          <p className="mt-2 text-[10px] font-mono break-all text-slate-500">{q.qr_uuid}</p>
                          {q.image && (
                            <a
                              href={q.image}
                              download={`${detail.htno || detail.id}_${label.toLowerCase().replace(" ", "")}.png`}
                              className="inline-flex items-center gap-1 mt-2 text-xs text-primary hover:underline"
                            >
                              <Download size={12} /> Download
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  data-testid="dialog-view-qr"
                  onClick={() => viewQR(detail)}
                >
                  <QrCode size={14} className="mr-1" /> View QR
                </Button>
                {!readOnly && (
                  <Button
                    data-testid="dialog-send-qr"
                    onClick={() => sendQR(detail)}
                    className="bg-primary hover:bg-primary/90"
                  >
                    <Send size={14} className="mr-1" /> Send QR
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!qrPreview} onOpenChange={(o) => { if (!o) setQrPreview(null); }}>
        <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="qr-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">QR Preview</DialogTitle>
          </DialogHeader>
          {qrPreview && (
            <div className="space-y-4">
              <div>
                <p className="font-medium text-slate-900">{qrPreview.student_name}</p>
                <p className="text-sm text-slate-500">{qrPreview.htno}</p>
              </div>
              {qrPreviewLoading ? (
                <div className="flex items-center justify-center gap-2 py-12 text-slate-500">
                  <Loader2 size={18} className="animate-spin" />
                  Generating QR passes…
                </div>
              ) : qrPreview.qrs?.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {qrPreview.qrs.map((q) => {
                    const label = q.caption || (q.qr_type === "GUEST" ? `Guest ${q.guest_index}` : "Student");
                    const downloadName = `${(qrPreview.htno || "qr").replace(/[^\w.-]+/g, "_")}_${q.qr_type === "STUDENT" ? "student" : `guest${q.guest_index || ""}`}.png`;
                    return (
                      <div
                        key={q.id || `${q.qr_type}-${q.guest_index || 0}`}
                        className="text-center border rounded-lg p-4"
                        data-testid={`qr-preview-${q.qr_type === "STUDENT" ? "student" : `guest-${q.guest_index}`}`}
                      >
                        <p className="text-sm font-semibold text-slate-800 mb-3">{label}</p>
                        {q.image && (
                          <img src={q.image} alt={label} className="mx-auto w-full max-w-[200px]" />
                        )}
                        <p className="mt-3 text-[10px] font-mono break-all text-slate-500">{q.qr_uuid}</p>
                        {q.image && (
                          <a
                            href={q.image}
                            download={downloadName}
                            className="inline-flex items-center gap-1 mt-3 text-xs text-primary hover:underline"
                          >
                            <Download size={12} /> Download PNG
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-slate-500 py-8 text-center">No QR passes could be generated for this registration.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen && !readOnly} onOpenChange={(o) => { if (!o && !bulkRunning && !bulkGenerating && !guestCountBusy) setBulkOpen(false); }}>
        <DialogContent className="max-w-4xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Bulk QR</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 text-sm">
            <p className="text-slate-600">
              Upload an Excel with HTNO and guest count to add guest slots and create QR passes for new guests only,
              generate missing QRs for the portal, or email passes in bulk. Existing QRs are never recreated.
            </p>

            <Tabs value={bulkTab} onValueChange={setBulkTab}>
              <TabsList className="grid w-full grid-cols-3 max-w-2xl">
                <TabsTrigger value="upload" data-testid="bulk-tab-upload">Upload guest counts</TabsTrigger>
                <TabsTrigger value="all" data-testid="bulk-tab-all">Send to all</TabsTrigger>
                <TabsTrigger value="select" data-testid="bulk-tab-select">Select students</TabsTrigger>
              </TabsList>

              <TabsContent value="upload" className="space-y-4 mt-4">
                <div className="rounded-lg border p-4 space-y-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <Button variant="outline" size="sm" onClick={downloadGuestCountTemplate} data-testid="guest-count-template-btn">
                      <FileDown size={14} className="mr-1" /> Download template
                    </Button>
                    <p className="text-xs text-slate-500">Columns: <b>HTNO</b>, <b>Guest Count</b></p>
                  </div>
                  <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                    <label className="inline-flex items-center gap-3 border border-dashed border-slate-300 rounded-md px-4 py-3 cursor-pointer hover:border-primary transition-colors">
                      <UploadCloud size={18} />
                      <span className="text-sm">{guestCountFile ? guestCountFile.name : "Choose .xlsx file"}</span>
                      <input
                        data-testid="guest-count-upload-file"
                        type="file"
                        accept=".xlsx"
                        className="hidden"
                        onChange={(e) => {
                          setGuestCountFile(e.target.files?.[0] || null);
                          setGuestCountReport(null);
                          setGuestCountCommitted(false);
                        }}
                      />
                    </label>
                    <Button
                      variant="outline"
                      disabled={!guestCountFile || guestCountBusy}
                      onClick={validateGuestCountUpload}
                      data-testid="guest-count-validate-btn"
                    >
                      {guestCountBusy && !guestCountReport ? (
                        <><Loader2 size={14} className="mr-1 animate-spin" /> Validating…</>
                      ) : (
                        "Validate upload"
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">
                    Only rows where guest count <strong>increases</strong> are applied. Existing guest QRs are kept;
                    new QR passes are created only for added guest slots. Guest counts are never reduced from this upload.
                  </p>
                </div>

                {guestCountReport && (
                  <div className="space-y-4" data-testid="guest-count-validation-report">
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline">Total rows: {guestCountReport.total}</Badge>
                      <Badge className="bg-emerald-600">To update: {guestCountReport.valid_count}</Badge>
                      <Badge className="bg-slate-500">Unchanged: {guestCountReport.unchanged_count}</Badge>
                      <Badge className="bg-red-600">Invalid: {guestCountReport.invalid_count}</Badge>
                    </div>

                    {guestCountReport.valid_count > 0 && (
                      <div className="border rounded-lg overflow-x-auto max-h-56">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>HTNO</TableHead>
                              <TableHead>Name</TableHead>
                              <TableHead>Current</TableHead>
                              <TableHead>New</TableHead>
                              <TableHead>Add</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {guestCountReport.valid_rows.map((row) => (
                              <TableRow key={`valid-${row.registration_id}`}>
                                <TableCell>{row.htno}</TableCell>
                                <TableCell>{row.student_name}</TableCell>
                                <TableCell>{row.current_guest_count}</TableCell>
                                <TableCell>{row.new_guest_count}</TableCell>
                                <TableCell className="text-emerald-700 font-medium">+{row.guests_to_add}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}

                    {guestCountReport.invalid_count > 0 && (
                      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-900">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertCircle size={16} />
                          <span className="font-medium">Invalid rows</span>
                        </div>
                        <ul className="text-xs space-y-1">
                          {guestCountReport.invalid_rows.slice(0, 8).map((row) => (
                            <li key={`invalid-${row.row_number}`}>
                              Row {row.row_number} ({row.htno || "—"}): {(row.errors || []).join("; ")}
                            </li>
                          ))}
                          {guestCountReport.invalid_rows.length > 8 && (
                            <li>…and {guestCountReport.invalid_rows.length - 8} more</li>
                          )}
                        </ul>
                      </div>
                    )}

                    {guestCountReport.valid_count > 0 && (
                      <Button
                        className="bg-primary hover:bg-primary/90"
                        disabled={guestCountBusy || guestCountCommitted}
                        onClick={commitGuestCountUpload}
                        data-testid="guest-count-commit-btn"
                      >
                        {guestCountBusy ? (
                          <><Loader2 size={14} className="mr-1 animate-spin" /> Applying…</>
                        ) : guestCountCommitted ? (
                          <><CheckCircle2 size={14} className="mr-1" /> Applied</>
                        ) : (
                          `Add guests & create QRs (${guestCountReport.valid_count})`
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="all" className="space-y-4 mt-4">
                <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border p-4">
                  <div className="flex items-center gap-3">
                    <Switch
                      id="bulk-include-sent"
                      checked={bulkIncludeSent}
                      disabled={bulkRunning}
                      onCheckedChange={setBulkIncludeSent}
                      data-testid="bulk-include-sent"
                    />
                    <label htmlFor="bulk-include-sent" className="cursor-pointer">
                      Include students already emailed successfully
                    </label>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="outline"
                      disabled={bulkRunning || bulkGenerating}
                      onClick={() => runBulkGenerate()}
                      data-testid="bulk-generate-all-btn"
                    >
                      {bulkGenerating ? (
                        <><Loader2 size={14} className="mr-1 animate-spin" /> Generating…</>
                      ) : (
                        <><QrCode size={14} className="mr-1" /> Generate missing (no email)</>
                      )}
                    </Button>
                    <Button
                      className="bg-primary hover:bg-primary/90"
                      disabled={bulkRunning || bulkGenerating}
                      onClick={() => runBulkSend()}
                      data-testid="bulk-send-all-btn"
                    >
                    {bulkRunning ? (
                      <><Loader2 size={14} className="mr-1 animate-spin" /> Sending…</>
                    ) : (
                      <><Send size={14} className="mr-1" /> Send to all attending</>
                    )}
                  </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="select" className="space-y-4 mt-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="relative w-full md:w-80">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      className="pl-8"
                      placeholder="Search roll no. or name…"
                      value={bulkSelectQ}
                      onChange={(e) => setBulkSelectQ(e.target.value)}
                      data-testid="bulk-select-search"
                    />
                  </div>
                  <p className="text-slate-500 text-xs shrink-0">
                    {bulkSelectedIds.size} selected · Page {bulkSelectPage} / {bulkSelectPages}
                  </p>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4">
                  <div className="flex items-center gap-3">
                    <Switch
                      id="bulk-select-include-sent"
                      checked={bulkIncludeSent}
                      disabled={bulkRunning}
                      onCheckedChange={setBulkIncludeSent}
                      data-testid="bulk-select-include-sent"
                    />
                    <label htmlFor="bulk-select-include-sent" className="cursor-pointer">
                      Include students already emailed successfully
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={bulkRunning || bulkGenerating || bulkSelectedIds.size === 0}
                      onClick={() => setBulkSelectedIds(new Set())}
                      data-testid="bulk-clear-selection"
                    >
                      Clear selection
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={bulkRunning || bulkGenerating || bulkSelectedIds.size === 0}
                      onClick={() => runBulkGenerate({ registrationIds: Array.from(bulkSelectedIds) })}
                      data-testid="bulk-generate-selected-btn"
                    >
                      {bulkGenerating ? (
                        <><Loader2 size={14} className="mr-1 animate-spin" /> Generating…</>
                      ) : (
                        <><QrCode size={14} className="mr-1" /> Generate missing ({bulkSelectedIds.size})</>
                      )}
                    </Button>
                    <Button
                      className="bg-primary hover:bg-primary/90"
                      disabled={bulkRunning || bulkGenerating || bulkSelectedIds.size === 0}
                      onClick={() => runBulkSend({ registrationIds: Array.from(bulkSelectedIds) })}
                      data-testid="bulk-send-selected-btn"
                    >
                      {bulkRunning ? (
                        <><Loader2 size={14} className="mr-1 animate-spin" /> Sending…</>
                      ) : (
                        <><Send size={14} className="mr-1" /> Send to selected ({bulkSelectedIds.size})</>
                      )}
                    </Button>
                  </div>
                </div>

                <div className="border rounded-lg overflow-x-auto max-h-80">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox
                            checked={bulkSelectPageAllSelected}
                            onCheckedChange={(v) => toggleBulkSelectPage(!!v)}
                            aria-label="Select all on page"
                            data-testid="bulk-select-all-page"
                          />
                        </TableHead>
                        <TableHead>Roll</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead className="text-center">QR Email</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bulkSelectLoading && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-slate-500">Loading…</TableCell>
                        </TableRow>
                      )}
                      {!bulkSelectLoading && bulkSelectRows.map((r) => (
                        <TableRow key={r.id} data-testid={`bulk-select-row-${r.htno}`}>
                          <TableCell>
                            <Checkbox
                              checked={bulkSelectedIds.has(r.id)}
                              onCheckedChange={(v) => toggleBulkSelect(r.id, !!v)}
                              aria-label={`Select ${r.htno}`}
                              data-testid={`bulk-select-${r.htno}`}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{r.htno}</TableCell>
                          <TableCell>{r.student_name}</TableCell>
                          <TableCell className="text-slate-500">{r.mu_email || r.email || "—"}</TableCell>
                          <TableCell className="text-center">
                            {r.qr_email_status ? (
                              <Badge className={QR_EMAIL_STATUS[r.qr_email_status]?.className || "bg-slate-500"}>
                                {QR_EMAIL_STATUS[r.qr_email_status]?.label || r.qr_email_status}
                              </Badge>
                            ) : (
                              <span className="text-slate-400 text-xs">Not sent</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {!bulkSelectLoading && bulkSelectRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                            No attending students found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex items-center justify-between">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={bulkSelectPage <= 1 || bulkSelectLoading}
                    onClick={() => setBulkSelectPage((p) => p - 1)}
                    data-testid="bulk-select-prev"
                  >
                    <ChevronLeft size={14} /> Prev
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={bulkSelectPage >= bulkSelectPages || bulkSelectLoading}
                    onClick={() => setBulkSelectPage((p) => p + 1)}
                    data-testid="bulk-select-next"
                  >
                    Next <ChevronRight size={14} />
                  </Button>
                </div>
              </TabsContent>
            </Tabs>

            {(bulkRunning || bulkGenerating || guestCountBusy) && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
                {guestCountBusy ? (
                  "Processing guest count upload — adding slots and creating QR passes for new guests only."
                ) : bulkGenerating ? (
                  "Creating missing QR passes only — existing QRs are not changed."
                ) : (
                  <>
                    Sending QR emails in small batches ({BULK_EMAIL_CHUNK_SIZE} at a time) to avoid timeouts.
                    {bulkResult ? (
                      <span className="block mt-1 font-medium">
                        Progress: {bulkResult.sent + bulkResult.failed + bulkResult.skipped} / {bulkResult.total}
                        {" "}({bulkResult.sent} sent, {bulkResult.failed} failed, {bulkResult.skipped} skipped)
                      </span>
                    ) : (
                      <span className="block mt-1">Preparing recipient list…</span>
                    )}
                  </>
                )}
              </div>
            )}

            {bulkResult && (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">Total: {bulkResult.total}</Badge>
                  <Badge className="bg-emerald-600">Sent: {bulkResult.sent}</Badge>
                  <Badge className="bg-red-600">Failed: {bulkResult.failed}</Badge>
                  <Badge className="bg-amber-600">Skipped: {bulkResult.skipped}</Badge>
                </div>
                <div className="border rounded-lg overflow-x-auto max-h-64">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Roll</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Error</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(bulkResult.items || []).map((item) => (
                        <TableRow key={`${item.registration_id}-${item.status}`}>
                          <TableCell>{item.htno}</TableCell>
                          <TableCell>{item.student_name}</TableCell>
                          <TableCell>
                            <Badge className={QR_EMAIL_STATUS[item.status]?.className || "bg-slate-500"}>
                              {QR_EMAIL_STATUS[item.status]?.label || item.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-500">{item.to_email || "—"}</TableCell>
                          <TableCell className="text-red-600 text-xs max-w-[220px] truncate">{item.error || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="kicker">Recent batches</p>
                <Button size="sm" variant="ghost" disabled={bulkLoadingBatches} onClick={loadBulkBatches}>
                  <RefreshCw size={14} className={bulkLoadingBatches ? "animate-spin" : ""} />
                </Button>
              </div>
              {bulkLoadingBatches && bulkBatches.length === 0 && (
                <p className="text-slate-500">Loading batches…</p>
              )}
              {!bulkLoadingBatches && bulkBatches.length === 0 && (
                <p className="text-slate-500">No bulk sends yet.</p>
              )}
              <div className="space-y-2">
                {bulkBatches.map((b) => (
                  <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 border rounded-md p-3">
                    <div>
                      <p className="font-medium text-xs font-mono">{b.id.slice(0, 8)}…</p>
                      <p className="text-slate-500 text-xs">{formatWhen(b.started_at)} · {b.status}</p>
                      <p className="text-xs mt-1">
                        {b.sent} sent · {b.failed} failed · {b.skipped} skipped · {b.total} total
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => loadBatchDetail(b.id)}>
                        View log
                      </Button>
                      {b.failed > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={bulkRunning}
                          onClick={() => runBulkSend({ resendFailedBatchId: b.id })}
                          data-testid={`resend-failed-${b.id.slice(0, 8)}`}
                        >
                          Resend failed ({b.failed})
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {bulkBatchDetail && (
              <div className="border-t pt-4 space-y-3">
                <p className="kicker">Batch log · {bulkBatchDetail.batch?.id?.slice(0, 8)}…</p>
                <div className="border rounded-lg overflow-x-auto max-h-72">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Roll</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>When</TableHead>
                        <TableHead>Error</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(bulkBatchDetail.items || []).map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.htno}</TableCell>
                          <TableCell>{item.student_name}</TableCell>
                          <TableCell>
                            <Badge className={QR_EMAIL_STATUS[item.status]?.className || "bg-slate-500"}>
                              {QR_EMAIL_STATUS[item.status]?.label || item.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-500">{item.to_email || "—"}</TableCell>
                          <TableCell className="text-xs text-slate-500">{formatWhen(item.created_at)}</TableCell>
                          <TableCell className="text-red-600 text-xs max-w-[200px] truncate">{item.error_message || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
